/**
 * Reusable drag-and-drop file picker used by every module's "Upload" step.
 * Lifted from IntakeUploadPage.js so all workflow uploads share the same UX.
 */
import React, { useCallback, useRef, useState } from 'react';

function fileKind(name) {
  const ext = String(name || '').split('.').pop().toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['xlsx', 'xls', 'csv', 'tsv'].includes(ext)) return 'sheet';
  if (['x12', 'edi'].includes(ext)) return 'edi';
  if (['json'].includes(ext)) return 'json';
  if (['zip'].includes(ext)) return 'zip';
  return 'text';
}

function fmtBytes(n) {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileDropZone({
  files,
  onFiles,
  accept = '',
  multiple = true,
  title = 'Click to choose files or drag & drop here',
  hint = '',
}) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const addFiles = useCallback((incoming) => {
    const next = Array.from(incoming || []).filter((f) => f && f.name);
    if (!next.length) return;
    onFiles(multiple ? [...(files || []), ...next] : [next[0]]);
  }, [files, onFiles, multiple]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer?.files);
  }, [addFiles]);

  const removeFile = (idx) => onFiles((files || []).filter((_, i) => i !== idx));

  return (
    <div>
      <div
        className={`wf-drop${dragActive ? ' is-active' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current && inputRef.current.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current && inputRef.current.click();
          }
        }}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
      >
        <div className="wf-drop__title">{title}</div>
        {hint && <div className="wf-drop__hint">{hint}</div>}
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept || undefined}
          style={{ display: 'none' }}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />
      </div>
      {(files || []).length > 0 && (
        <ul className="wf-filelist" style={{ marginTop: 10 }}>
          {files.map((f, idx) => (
            <li key={`${f.name}-${idx}`} className="wf-filelist__item">
              <span>
                <span className="wf-filelist__kind">{fileKind(f.name)}</span>
                {f.name}
                {f.size ? <span style={{ color: '#94a3b8', marginLeft: 8 }}>{fmtBytes(f.size)}</span> : null}
              </span>
              <button type="button" className="wf-filelist__remove" onClick={() => removeFile(idx)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
